import { useState, useEffect } from 'react'
import { withRequiredPermission } from '@/components/auth/with-required-permission'
import { PERMISSIONS } from '@/hooks/use-perm'
import { useNavigate } from 'react-router-dom'
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id'
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit'
import { useTranslation } from 'react-i18next'
import { AlertTriangle } from 'lucide-react'
import { useProcessorById, useUpdateProcessor } from '@/lib/api/processors'
import { ProcessorEditHeader } from '../../components/processor-edit-header'
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { SecretInput } from '@/components/form/secret-input'
import { useToast } from '@/components/ui/use-toast'

const MollieEditPageInner = () => {
    const id = useProcessorEditId()
    const navigate = useNavigate()
    const processorExit = useProcessorEditExit()
    const { t } = useTranslation('processors')
    const { toast } = useToast()

    const { data: processor, isLoading } = useProcessorById(id)
    const updateProcessorMutation = useUpdateProcessor()

    const [validationError, setValidationError] = useState<string | null>(null)
    const [formData, setFormData] = useState({
        name: '',
        enabled: true,
        dryMode: false,
        apiKey: '',
    })

    useEffect(() => {
        if (processor) {
            const options = processor.options as any
            setFormData({
                name: processor.name,
                enabled: processor.enabled ?? true,
                dryMode: options?.dryMode === true,
                apiKey: options?.apiKey || '',
            })
        }
    }, [processor])

    const handleSubmit = async () => {
        if (!formData.name.trim()) { setValidationError(t('nameRequired')); return }
        if (!formData.apiKey.trim()) { setValidationError(t('mollie.validation.apiKeyRequired')); return }

        setValidationError(null)
        try {
            await updateProcessorMutation.mutateAsync({
                processorId: processor!.id,
                updatedData: {
                    name: formData.name,
                    type: 'mollie',
                    enabled: formData.enabled,
                    options: {
                        dryMode: formData.dryMode,
                        apiKey: formData.apiKey,
                    },
                } as any,
            })
            toast({ description: t('processorUpdated') })
            if (processorExit.isEmbed) {
                processorExit.exitAfterSave()
                return
            }
            navigate(-1)
        } catch {
            toast({ description: t('failedToSave'), variant: 'destructive' })
        }
    }

    if (isLoading) return null

    if (!processor || processor.type !== 'mollie') {
        return (
            <div className="container mx-auto py-8">
                <Alert variant="destructive">
                    <AlertDescription>{t('processorNotFound')}</AlertDescription>
                </Alert>
            </div>
        )
    }

    return (
        <div className="container mx-auto py-8">
            <ProcessorEditHeader
                processorId={processor.id}
                title={t('mollie.editTitle')}
                onCancel={() => (processorExit.isEmbed ? processorExit.exitCancel() : navigate(-1))}
                onSave={handleSubmit}
                isSaving={updateProcessorMutation.isPending}
            />

            {validationError && (
                <Alert variant="destructive" className="mb-6">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{validationError}</AlertDescription>
                </Alert>
            )}

            <div className="space-y-6">
                <Card>
                    <CardHeader><CardTitle>{t('generalSettings')}</CardTitle></CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label htmlFor="name">{t('processorName')}</Label>
                            <Input id="name" value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                        </div>
                        <div className="flex items-center space-x-2">
                            <Checkbox id="enabled" checked={formData.enabled}
                                onCheckedChange={(c) => setFormData({ ...formData, enabled: c === true })} />
                            <Label htmlFor="enabled">{t('enabled')}</Label>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader><CardTitle>{t('apiCredentials')}</CardTitle></CardHeader>
                    <CardContent className="space-y-4">
                        <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
                            <p className="text-sm text-blue-700">
                                <strong>{t('mollie.credentials.howToGetTitle')}</strong>
                            </p>
                            <ol className="mt-2 list-decimal pl-4 text-sm text-blue-700">
                                <li>{t('mollie.credentials.step1')}</li>
                                <li>{t('mollie.credentials.step2')}</li>
                                <li>{t('mollie.credentials.step3')}</li>
                                <li>{t('mollie.credentials.step4')}</li>
                            </ol>
                        </div>
                        <SecretInput label={t('mollie.credentials.apiKeyLabel') + ' *'} value={formData.apiKey}
                            onChange={(v) => setFormData({ ...formData, apiKey: v })}
                            placeholder={t('mollie.credentials.apiKeyPlaceholder')} />
                        <p className="text-muted-foreground text-xs">
                            {t('mollie.credentials.note')}
                        </p>
                    </CardContent>
                </Card>

                <ProcessorDryModeField
                    checked={formData.dryMode}
                    onCheckedChange={(c) => setFormData({ ...formData, dryMode: c })}
                    disabled={updateProcessorMutation.isPending}
                />
            </div>
        </div>
    )
}

export const MollieEditPage = withRequiredPermission(MollieEditPageInner, PERMISSIONS.PROCESSORS_MANAGE)
